import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useSessionComms, SESSION_CONTEXT_CHANGED_EVENT } from '../contexts/SessionCommsContext';
import { useAuth } from '../contexts/AuthContext';
import { ChatIcon, MicIcon, UsersIcon } from './icons/AppIcons';
import { sessionApi } from '../services/sessionApi';
import storageManager from '../utils/StorageManager';
import { buildAutoJoinAlias, generateRandomGuestAlias, isPlayerOneName } from '../utils/guestAlias';
import { clearSessionGameStateCaches } from '../utils/sessionGameState';

const tabIconMap = {
    party: UsersIcon,
    chat: ChatIcon,
    voice: MicIcon
};

const HOST_SESSION_CACHE_KEY = 'guest_host_active_session';
const HOST_SESSION_CACHE_TTL_HOURS = 2;
const ACTIVE_SESSION_CONTEXT_KEY = 'guest_active_session_context';
const ACTIVE_SESSION_CONTEXT_TTL_HOURS = 2;
const SESSION_PRESENCE_TTL_HOURS = 2;

function SocialButton() {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('party');
    const [hostAlias, setHostAlias] = useState('');
    const [joinCode, setJoinCode] = useState('');
    const savedDefaultJoinName = storageManager.getPlayerName();
    const [joinName, setJoinName] = useState(() => savedDefaultJoinName);
    const [chatInput, setChatInput] = useState('');
    const [partySession, setPartySession] = useState(null);
    const [activeSessionContext, setActiveSessionContext] = useState(null);
    const [partyError, setPartyError] = useState('');
    const [partyLoading, setPartyLoading] = useState(false);
    const [joinAlias, setJoinAlias] = useState('');
    const [transferHostAlias, setTransferHostAlias] = useState('');
    const [sendingChat, setSendingChat] = useState(false);
    const [autoJoinAliasSeed] = useState(() => generateRandomGuestAlias());
    const { theme, currentTheme } = useTheme();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const {
        activeSessionContext: liveSessionContext,
        session: liveSession,
        sessionJoinLocked,
        sessionError,
        presenceStatus,
        messages,
        sendChatMessage,
        setLobbyLocked,
        voiceEnabled,
        localMuted,
        deafened,
        localSpeaking,
        remoteMuteStatus,
        activeSpeakers,
        remoteVoices,
        emitRealtimeEvent,
        realtimeEnabled,
        realtimeStatus,
        enableVoice,
        disableVoice,
        toggleMute,
        toggleDeafen,
        hasTurnRelay,
    } = useSessionComms();
    const isLightTheme = currentTheme === 'light';
    const launcherBackground = isLightTheme ? theme.accent : theme.secondary;
    const launcherBorder = isLightTheme ? theme.secondary : theme.light;
    const launcherIconColor = isLightTheme ? theme.bgDark : theme.textPrimary;
    const resolvedSessionContext = liveSessionContext || activeSessionContext;
    const hasActiveSessionContext = Boolean(resolvedSessionContext?.code);
    const isHostContext = resolvedSessionContext?.role === 'host';
    const activeParticipants = liveSession?.participants || [];
    const connectedParticipants = activeParticipants.filter((participant) => participant.connected);
    const currentHostAlias = String(liveSession?.hostAlias || '').trim()
        || String(resolvedSessionContext?.alias || '').trim()
        || String(partySession?.hostAlias || '').trim();
    const transferCandidates = activeParticipants.filter((participant) => (
        String(participant.alias || '').trim().toLowerCase() !== currentHostAlias.toLowerCase()
    ));

    const tabs = [
        { id: 'party', label: 'Party' },
        { id: 'chat', label: 'Chat' },
        { id: 'voice', label: 'Voice' }
    ];

    const getSessionPresenceKey = (sessionCode) => `guest_session_presence_${String(sessionCode || '').trim().toUpperCase()}`;

    const saveSessionPresence = (sessionCode, alias) => {
        const normalizedCode = String(sessionCode || '').trim().toUpperCase();
        const normalizedAlias = String(alias || '').trim();
        if (!normalizedCode || !normalizedAlias) return;
        storageManager.saveTemporaryData(
            getSessionPresenceKey(normalizedCode),
            { alias: normalizedAlias },
            SESSION_PRESENCE_TTL_HOURS
        );
    };

    const clearSessionPresence = (sessionCode) => {
        const normalizedCode = String(sessionCode || '').trim().toUpperCase();
        if (!normalizedCode) return;
        storageManager.removeTemporaryData(getSessionPresenceKey(normalizedCode));
    };

    const clearActiveSessionContext = () => {
        setActiveSessionContext(null);
        storageManager.removeTemporaryData(ACTIVE_SESSION_CONTEXT_KEY);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(SESSION_CONTEXT_CHANGED_EVENT));
        }
    };

    const saveActiveSessionContext = (context) => {
        setActiveSessionContext(context);
        storageManager.saveTemporaryData(
            ACTIVE_SESSION_CONTEXT_KEY,
            context,
            ACTIVE_SESSION_CONTEXT_TTL_HOURS
        );
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(SESSION_CONTEXT_CHANGED_EVENT));
        }
    };

    useEffect(() => {
        if (liveSessionContext?.code) {
            setActiveSessionContext(liveSessionContext);
        }
    }, [liveSessionContext]);

    useEffect(() => {
        if (authLoading) return;
        const normalizedJoinName = String(joinName || '').trim();
        const shouldAssignAuto = !normalizedJoinName
            || isPlayerOneName(normalizedJoinName)
            || (!isAuthenticated && normalizedJoinName === String(savedDefaultJoinName || '').trim());
        if (!shouldAssignAuto) return;
        setJoinName(autoJoinAliasSeed);
    }, [authLoading, autoJoinAliasSeed, isAuthenticated, joinName, savedDefaultJoinName]);

    useEffect(() => {
        if (!transferHostAlias) return;
        const exists = transferCandidates.some((participant) => (
            String(participant.alias || '').trim().toLowerCase() === transferHostAlias.toLowerCase()
        ));
        if (!exists) {
            setTransferHostAlias('');
        }
    }, [transferCandidates, transferHostAlias]);

    useEffect(() => {
        if (sessionError) {
            setPartyError(sessionError);
        }
    }, [sessionError]);

    useEffect(() => {
        let mounted = true;

        const restoreSessionContext = async () => {
            const cachedHost = storageManager.getTemporaryData(HOST_SESSION_CACHE_KEY);
            const cachedContext = storageManager.getTemporaryData(ACTIVE_SESSION_CONTEXT_KEY);

            const candidates = [];
            const hostCode = String(cachedHost?.code || '').trim().toUpperCase();
            if (hostCode) {
                candidates.push({
                    code: hostCode,
                    role: 'host',
                    alias: String(cachedHost?.alias || '').trim() || undefined,
                });
            }

            const contextCode = String(cachedContext?.code || '').trim().toUpperCase();
            if (contextCode && !candidates.some((candidate) => candidate.code === contextCode)) {
                candidates.push({
                    code: contextCode,
                    role: cachedContext?.role === 'host' ? 'host' : 'participant',
                    alias: String(cachedContext?.alias || '').trim() || undefined,
                });
            }

            if (!candidates.length) {
                return;
            }

            for (const candidate of candidates) {
                try {
                    const data = await sessionApi.getGuestSession(candidate.code);
                    if (!mounted) return;
                    const restoredSession = data.session || null;
                    if (!restoredSession || restoredSession.isExpired) {
                        continue;
                    }

                    if (candidate.role === 'host') {
                        setPartySession(restoredSession);
                        setHostAlias(restoredSession.hostAlias || '');
                        saveSessionPresence(restoredSession.code, restoredSession.hostAlias || candidate.alias || 'Host');
                        storageManager.saveTemporaryData(
                            HOST_SESSION_CACHE_KEY,
                            { code: restoredSession.code, alias: restoredSession.hostAlias || '' },
                            HOST_SESSION_CACHE_TTL_HOURS
                        );
                        saveActiveSessionContext({
                            code: restoredSession.code,
                            alias: restoredSession.hostAlias || candidate.alias || 'Host',
                            role: 'host',
                            joinUrl: restoredSession.joinUrl,
                        });
                    } else {
                        saveActiveSessionContext({
                            code: restoredSession.code,
                            alias: candidate.alias || 'Guest',
                            role: 'participant',
                            joinUrl: restoredSession.joinUrl,
                        });
                    }
                    return;
                } catch (error) {
                    if (error?.status !== 404 && error?.status !== 410) {
                        saveActiveSessionContext({
                            code: candidate.code,
                            alias: candidate.alias || (candidate.role === 'host' ? 'Host' : 'Guest'),
                            role: candidate.role,
                            joinUrl: `${window.location.origin}/session/${candidate.code}`,
                        });
                        return;
                    }
                }
            }

            storageManager.removeTemporaryData(HOST_SESSION_CACHE_KEY);
            storageManager.removeTemporaryData(ACTIVE_SESSION_CONTEXT_KEY);
            setPartySession(null);
            setActiveSessionContext(null);
        };

        restoreSessionContext();

        return () => {
            mounted = false;
        };
    }, []);

    const createSession = async () => {
        setPartyError('');
        setJoinAlias('');
        if (hasActiveSessionContext) {
            setPartyError('You already have an active session. Leave or end it before starting another.');
            return;
        }

        try {
            setPartyLoading(true);
            const data = await sessionApi.createGuestSession({
                hostAlias: hostAlias.trim() || undefined,
            });
            const nextSession = data.session || null;
            setPartySession(nextSession);
            if (nextSession?.code) {
                saveSessionPresence(nextSession.code, nextSession.hostAlias || hostAlias.trim() || 'Host');
                storageManager.saveTemporaryData(
                    HOST_SESSION_CACHE_KEY,
                    { code: nextSession.code, alias: nextSession.hostAlias || hostAlias.trim() || 'Host' },
                    HOST_SESSION_CACHE_TTL_HOURS
                );
                saveActiveSessionContext({
                    code: nextSession.code,
                    alias: nextSession.hostAlias || hostAlias.trim() || 'Host',
                    role: 'host',
                    joinUrl: nextSession.joinUrl,
                });
                emitRealtimeEvent('session-updated', {
                    code: nextSession.code,
                    action: 'created',
                }, nextSession.code).catch(() => null);
            }
        } catch (error) {
            if (error?.status === 409 && error?.data?.session) {
                const existingSession = error.data.session;
                setPartySession(existingSession);
                setHostAlias(existingSession.hostAlias || hostAlias);
                const cachedHost = storageManager.getTemporaryData(HOST_SESSION_CACHE_KEY);
                const cachedHostCode = String(cachedHost?.code || '').trim().toUpperCase();
                const roleHint = error?.data?.ownership === 'host' ? 'host' : (error?.data?.ownership === 'participant' ? 'participant' : null);
                const role = roleHint || (cachedHostCode === String(existingSession.code || '').toUpperCase() ? 'host' : 'participant');
                if (role === 'host') {
                    saveSessionPresence(existingSession.code, existingSession.hostAlias || hostAlias.trim() || 'Host');
                    storageManager.saveTemporaryData(
                        HOST_SESSION_CACHE_KEY,
                        { code: existingSession.code, alias: existingSession.hostAlias || hostAlias.trim() || 'Host' },
                        HOST_SESSION_CACHE_TTL_HOURS
                    );
                } else {
                    storageManager.removeTemporaryData(HOST_SESSION_CACHE_KEY);
                    setPartySession(null);
                }
                saveActiveSessionContext({
                    code: existingSession.code,
                    alias: role === 'host'
                        ? (existingSession.hostAlias || 'Host')
                        : (resolvedSessionContext?.alias || 'Guest'),
                    role,
                    joinUrl: existingSession.joinUrl,
                });
                setPartyError('You already have an active session. Leave or end it before creating another.');
                return;
            }
            setPartyError(error.message || 'Failed to create session');
        } finally {
            setPartyLoading(false);
        }
    };

    const endSession = async () => {
        const activeCode = String(partySession?.code || resolvedSessionContext?.code || '').trim().toUpperCase();
        const activeHostAlias = String(resolvedSessionContext?.alias || partySession?.hostAlias || hostAlias || '').trim();
        if (!activeCode) return;
        setPartyError('');
        setJoinAlias('');
        try {
            setPartyLoading(true);
            await sessionApi.endGuestSession(activeCode, { hostAlias: activeHostAlias || undefined });
            emitRealtimeEvent('session-updated', {
                code: activeCode,
                action: 'ended',
            }, activeCode).catch(() => null);
            emitRealtimeEvent('messages-updated', {
                code: activeCode,
                action: 'ended',
            }, activeCode).catch(() => null);
            clearSessionPresence(activeCode);
            clearSessionGameStateCaches(activeCode);
            setPartySession(null);
            storageManager.removeTemporaryData(HOST_SESSION_CACHE_KEY);
            clearActiveSessionContext();
            setJoinAlias('Session ended. You can create a new one.');
        } catch (error) {
            setPartyError(error.message || 'Failed to end session');
        } finally {
            setPartyLoading(false);
        }
    };

    const leaveCurrentSession = async () => {
        if (!resolvedSessionContext?.code || !resolvedSessionContext?.alias) return;
        setPartyError('');
        setJoinAlias('');
        try {
            setPartyLoading(true);
            await sessionApi.leaveGuestSession(resolvedSessionContext.code, {
                alias: resolvedSessionContext.alias,
            });
            emitRealtimeEvent('session-updated', {
                code: resolvedSessionContext.code,
                action: 'left',
                alias: resolvedSessionContext.alias,
            }, resolvedSessionContext.code).catch(() => null);
            emitRealtimeEvent('messages-updated', {
                code: resolvedSessionContext.code,
                action: 'left',
                alias: resolvedSessionContext.alias,
            }, resolvedSessionContext.code).catch(() => null);
            clearSessionPresence(resolvedSessionContext.code);
            clearSessionGameStateCaches(resolvedSessionContext.code);
            clearActiveSessionContext();
            setPartySession(null);
            setJoinAlias('Left session. You can now join or create another.');
        } catch (error) {
            setPartyError(error.message || 'Failed to leave session');
        } finally {
            setPartyLoading(false);
        }
    };

    const joinFromPopup = async () => {
        if (hasActiveSessionContext) {
            setPartyError('Leave or end your active session before joining another.');
            return;
        }
        if (!joinCode.trim()) {
            setPartyError('Enter a session code to join.');
            return;
        }

        const codeOnly = joinCode.trim().toUpperCase().replace(/^.*\/session\//i, '');
        setJoinCode(codeOnly);
        setPartyError('');
        setJoinAlias('');
        try {
            setPartyLoading(true);
            const preJoinSessionData = await sessionApi.getGuestSession(codeOnly);
            const preJoinSession = preJoinSessionData.session || null;
            const activeAliases = Array.isArray(preJoinSession?.participants)
                ? preJoinSession.participants.map((participant) => participant.alias)
                : [];
            const resolvedAlias = buildAutoJoinAlias({
                typedAlias: joinName,
                storedAlias: savedDefaultJoinName,
                isAuthenticated,
                activeAliases,
            });
            setJoinName(resolvedAlias);

            const joinData = await sessionApi.joinGuestSession(codeOnly, { alias: resolvedAlias });
            const sessionData = await sessionApi.getGuestSession(codeOnly);
            const joinedSession = sessionData.session || null;
            const finalAlias = joinData.participant?.alias || resolvedAlias;
            const isHostJoin = Boolean(joinedSession?.hostAlias)
                && joinedSession.hostAlias.toLowerCase() === finalAlias.toLowerCase();

            storageManager.savePlayerName(finalAlias);
            saveSessionPresence(codeOnly, finalAlias);
            saveActiveSessionContext({
                code: codeOnly,
                alias: finalAlias,
                role: isHostJoin ? 'host' : 'participant',
                joinUrl: joinedSession?.joinUrl || `${window.location.origin}/session/${codeOnly}`,
            });
            emitRealtimeEvent('session-updated', {
                code: codeOnly,
                action: 'joined',
                alias: finalAlias,
            }, codeOnly).catch(() => null);
            emitRealtimeEvent('messages-updated', {
                code: codeOnly,
                action: 'joined',
                alias: finalAlias,
            }, codeOnly).catch(() => null);
            setHostAlias(finalAlias);
            setPartySession(isHostJoin ? joinedSession : null);
            setJoinAlias(`Joined ${codeOnly} as ${finalAlias}.`);
            setActiveTab('chat');
        } catch (error) {
            setPartyError(error.message || 'Failed to join session');
        } finally {
            setPartyLoading(false);
        }
    };

    const openCurrentSession = () => {
        const activeCode = String(resolvedSessionContext?.code || '').trim().toUpperCase();
        if (!activeCode) return;
        navigate(`/session/${activeCode}`);
        setIsOpen(false);
    };

    const copySessionLink = async () => {
        const joinUrl = resolvedSessionContext?.joinUrl || partySession?.joinUrl || liveSession?.joinUrl;
        if (!joinUrl) return;
        try {
            await navigator.clipboard.writeText(joinUrl);
            setJoinAlias('Copied link to clipboard.');
        } catch {
            setJoinAlias('Copy failed. Select and copy manually.');
        }
    };

    const handleSendChat = async () => {
        if (!chatInput.trim()) return;
        setPartyError('');
        try {
            setSendingChat(true);
            await sendChatMessage(chatInput.trim());
            setChatInput('');
        } catch (error) {
            setPartyError(error.message || 'Failed to send message');
        } finally {
            setSendingChat(false);
        }
    };

    const handleToggleLobbyLock = async () => {
        if (!isHostContext) return;
        setPartyError('');
        try {
            setPartyLoading(true);
            await setLobbyLocked(!sessionJoinLocked);
        } catch (error) {
            setPartyError(error.message || 'Failed to update join lock');
        } finally {
            setPartyLoading(false);
        }
    };

    const handleTransferHost = async () => {
        const activeCode = String(resolvedSessionContext?.code || '').trim().toUpperCase();
        const callerAlias = String(resolvedSessionContext?.alias || '').trim();
        if (!activeCode || !callerAlias || !isHostContext) return;
        if (!transferHostAlias) {
            setPartyError('Select a participant to transfer host.');
            return;
        }

        setPartyError('');
        setJoinAlias('');
        try {
            setPartyLoading(true);
            const data = await sessionApi.transferGuestSessionHost(activeCode, {
                hostAlias: callerAlias,
                nextHostAlias: transferHostAlias,
            });
            const updatedSession = data.session || null;
            const nextHost = String(updatedSession?.hostAlias || transferHostAlias).trim();
            const localAlias = callerAlias;
            const nextRole = nextHost.toLowerCase() === localAlias.toLowerCase() ? 'host' : 'participant';
            const nextContext = {
                code: activeCode,
                alias: localAlias,
                role: nextRole,
                joinUrl: updatedSession?.joinUrl || resolvedSessionContext?.joinUrl || `${window.location.origin}/session/${activeCode}`,
            };
            saveActiveSessionContext(nextContext);

            if (nextRole === 'host') {
                saveSessionPresence(activeCode, localAlias);
                storageManager.saveTemporaryData(
                    HOST_SESSION_CACHE_KEY,
                    { code: activeCode, alias: localAlias },
                    HOST_SESSION_CACHE_TTL_HOURS
                );
            } else {
                storageManager.removeTemporaryData(HOST_SESSION_CACHE_KEY);
            }

            setPartySession(nextRole === 'host' ? updatedSession : null);
            setHostAlias(nextHost || localAlias);
            setTransferHostAlias('');
            setJoinAlias(`Host transferred to ${nextHost}.`);
            emitRealtimeEvent('session-updated', {
                code: activeCode,
                action: 'host-transferred',
                fromAlias: localAlias,
                toAlias: nextHost,
            }, activeCode).catch(() => null);
            emitRealtimeEvent('messages-updated', {
                code: activeCode,
                action: 'host-transferred',
                fromAlias: localAlias,
                toAlias: nextHost,
            }, activeCode).catch(() => null);
        } catch (error) {
            setPartyError(error.message || 'Failed to transfer host');
        } finally {
            setPartyLoading(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: launcherBackground,
                    border: `2px solid ${launcherBorder}`,
                    color: launcherIconColor,
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isLightTheme
                        ? '0 8px 24px rgba(57, 62, 70, 0.2)'
                        : '0 8px 24px rgba(0, 0, 0, 0.35)',
                    transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                aria-label="Open social panel"
            >
                <UsersIcon size={24} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setIsOpen(false)}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 1001
                            }}
                        />

                        <motion.div
                            initial={{ scale: 0, opacity: 0, transformOrigin: 'bottom right' }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                            style={{
                                position: 'fixed',
                                bottom: '80px',
                                right: '20px',
                                width: '350px',
                                maxWidth: 'calc(100vw - 40px)',
                                maxHeight: 'calc(100vh - 120px)',
                                backgroundColor: theme.bgMedium,
                                borderRadius: '15px',
                                zIndex: 1002,
                                boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                border: `2px solid ${theme.secondary}`
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '15px',
                                borderBottom: `1px solid ${theme.bgLight}`,
                                flexShrink: 0
                            }}>
                                <h2 style={{ margin: 0, fontSize: '1.3rem', color: theme.textPrimary }}>Social</h2>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: theme.textPrimary,
                                        fontSize: '1.5rem',
                                        cursor: 'pointer',
                                        padding: 0,
                                        width: '30px',
                                        height: '30px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    aria-label="Close social panel"
                                >
                                    &times;
                                </button>
                            </div>

                            <div style={{
                                display: 'flex',
                                borderBottom: `2px solid ${theme.bgLight}`,
                                backgroundColor: theme.bgDark,
                                flexShrink: 0
                            }}>
                                {tabs.map((tab) => {
                                    const TabIcon = tabIconMap[tab.id];
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            style={{
                                                flex: 1,
                                                padding: '12px 8px',
                                                fontSize: '0.9rem',
                                                backgroundColor: activeTab === tab.id ? theme.bgMedium : 'transparent',
                                                color: activeTab === tab.id ? theme.textPrimary : theme.textSecondary,
                                                border: 'none',
                                                borderBottom: activeTab === tab.id ? `3px solid ${theme.secondary}` : '3px solid transparent',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                fontFamily: 'inherit',
                                                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '5px',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <TabIcon size={20} />
                                            <span style={{ fontSize: '0.8rem' }}>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div style={{
                                flex: 1,
                                padding: '15px',
                                overflowY: 'auto',
                                minHeight: 0,
                                color: theme.textPrimary
                            }}>
                                {activeTab === 'party' && (
                                    <div>
                                        <h3 style={{ marginTop: 0, fontSize: '1.1rem', color: theme.textPrimary }}>Guest Party Session</h3>
                                        <p style={{ color: theme.textSecondary, fontSize: '0.9rem', marginTop: 0 }}>
                                            Create a join link without sign-in. Session expires after 90 minutes.
                                        </p>

                                        {hasActiveSessionContext ? (
                                            <div style={{
                                                backgroundColor: theme.bgDark,
                                                border: `1px solid ${theme.bgLight}`,
                                                borderRadius: '10px',
                                                padding: '10px',
                                                marginBottom: '10px'
                                            }}>
                                                <div style={{ fontSize: '0.85rem', color: theme.textSecondary }}>Current Session</div>
                                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '4px' }}>
                                                    {resolvedSessionContext.code}
                                                </div>
                                                <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>
                                                    {isHostContext
                                                        ? `Hosting as ${resolvedSessionContext.alias || 'Host'}`
                                                        : `Joined as ${resolvedSessionContext.alias || 'Guest'}`}
                                                </div>
                                                <div style={{ fontSize: '0.8rem', color: theme.textSecondary, marginTop: '4px' }}>
                                                    Join: {sessionJoinLocked ? 'Locked' : 'Open'} • Online: {connectedParticipants.length}/{activeParticipants.length || 0}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: theme.textSecondary, marginTop: '2px' }}>
                                                    Presence: {presenceStatus} • Realtime: {realtimeEnabled ? realtimeStatus : 'fallback'}
                                                </div>

                                                <button
                                                    onClick={openCurrentSession}
                                                    style={{
                                                        marginTop: '8px',
                                                        width: '100%',
                                                        padding: '8px',
                                                        borderRadius: '6px',
                                                        border: `1px solid ${theme.secondary}`,
                                                        backgroundColor: 'transparent',
                                                        color: theme.textPrimary,
                                                        cursor: 'pointer',
                                                        fontFamily: 'inherit',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Open Current Session
                                                </button>

                                                <button
                                                    onClick={copySessionLink}
                                                    style={{
                                                        marginTop: '8px',
                                                        width: '100%',
                                                        padding: '8px',
                                                        borderRadius: '6px',
                                                        border: `1px solid ${theme.secondary}`,
                                                        backgroundColor: 'transparent',
                                                        color: theme.textPrimary,
                                                        cursor: 'pointer',
                                                        fontFamily: 'inherit',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Copy Join Link
                                                </button>

                                                {isHostContext && (
                                                    <button
                                                        onClick={handleToggleLobbyLock}
                                                        disabled={partyLoading}
                                                        style={{
                                                            marginTop: '8px',
                                                            width: '100%',
                                                            padding: '8px',
                                                            borderRadius: '6px',
                                                            border: `1px solid ${theme.warning}`,
                                                            backgroundColor: 'transparent',
                                                            color: theme.warning,
                                                            cursor: partyLoading ? 'not-allowed' : 'pointer',
                                                            fontFamily: 'inherit',
                                                            fontWeight: 'bold',
                                                            opacity: partyLoading ? 0.7 : 1
                                                        }}
                                                    >
                                                        {sessionJoinLocked ? 'Unlock New Joins' : 'Lock New Joins'}
                                                    </button>
                                                )}

                                                {isHostContext && (
                                                    <div style={{
                                                        marginTop: '8px',
                                                        display: 'grid',
                                                        gap: '6px',
                                                        border: `1px solid ${theme.bgLight}`,
                                                        borderRadius: '8px',
                                                        padding: '8px',
                                                        backgroundColor: theme.bgMedium
                                                    }}>
                                                        <label style={{ fontSize: '0.78rem', color: theme.textSecondary }}>
                                                            Transfer Host
                                                        </label>
                                                        <select
                                                            value={transferHostAlias}
                                                            onChange={(event) => setTransferHostAlias(event.target.value)}
                                                            disabled={partyLoading || transferCandidates.length === 0}
                                                            style={{
                                                                padding: '8px',
                                                                borderRadius: '6px',
                                                                border: `1px solid ${theme.secondary}`,
                                                                backgroundColor: theme.bgDark,
                                                                color: theme.textPrimary,
                                                                fontFamily: 'inherit'
                                                            }}
                                                        >
                                                            <option value="">
                                                                {transferCandidates.length ? 'Select participant...' : 'No eligible participants'}
                                                            </option>
                                                            {transferCandidates.map((participant) => (
                                                                <option key={participant.id} value={participant.alias}>
                                                                    {participant.alias}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            onClick={handleTransferHost}
                                                            disabled={partyLoading || !transferHostAlias}
                                                            style={{
                                                                padding: '8px',
                                                                borderRadius: '6px',
                                                                border: `1px solid ${theme.warning}`,
                                                                backgroundColor: 'transparent',
                                                                color: theme.warning,
                                                                cursor: partyLoading || !transferHostAlias ? 'not-allowed' : 'pointer',
                                                                fontFamily: 'inherit',
                                                                fontWeight: 'bold',
                                                                opacity: partyLoading || !transferHostAlias ? 0.7 : 1
                                                            }}
                                                        >
                                                            Transfer Host
                                                        </button>
                                                    </div>
                                                )}

                                                {isHostContext ? (
                                                    <button
                                                        onClick={endSession}
                                                        disabled={partyLoading}
                                                        style={{
                                                            marginTop: '8px',
                                                            width: '100%',
                                                            padding: '8px',
                                                            borderRadius: '6px',
                                                            border: `1px solid ${theme.danger}`,
                                                            backgroundColor: 'transparent',
                                                            color: theme.danger,
                                                            cursor: partyLoading ? 'not-allowed' : 'pointer',
                                                            fontFamily: 'inherit',
                                                            fontWeight: 'bold',
                                                            opacity: partyLoading ? 0.7 : 1
                                                        }}
                                                    >
                                                        End Current Session
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={leaveCurrentSession}
                                                        disabled={partyLoading}
                                                        style={{
                                                            marginTop: '8px',
                                                            width: '100%',
                                                            padding: '8px',
                                                            borderRadius: '6px',
                                                            border: `1px solid ${theme.danger}`,
                                                            backgroundColor: 'transparent',
                                                            color: theme.danger,
                                                            cursor: partyLoading ? 'not-allowed' : 'pointer',
                                                            fontFamily: 'inherit',
                                                            fontWeight: 'bold',
                                                            opacity: partyLoading ? 0.7 : 1
                                                        }}
                                                    >
                                                        Leave Current Session
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
                                                    <input
                                                        value={hostAlias}
                                                        onChange={(event) => setHostAlias(event.target.value)}
                                                        placeholder="Host alias (optional)"
                                                        style={{
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            border: `2px solid ${theme.secondary}`,
                                                            backgroundColor: theme.bgDark,
                                                            color: theme.textPrimary,
                                                            fontFamily: 'inherit'
                                                        }}
                                                    />
                                                    <button
                                                        onClick={createSession}
                                                        disabled={partyLoading}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px',
                                                            fontSize: '0.95rem',
                                                            backgroundColor: theme.accent,
                                                            color: theme.textPrimary,
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: partyLoading ? 'not-allowed' : 'pointer',
                                                            fontFamily: 'inherit',
                                                            fontWeight: 'bold',
                                                            opacity: partyLoading ? 0.7 : 1
                                                        }}
                                                    >
                                                        {partyLoading ? 'Creating...' : 'Create 90-Min Session Link'}
                                                    </button>
                                                </div>

                                                <form
                                                    onSubmit={(event) => {
                                                        event.preventDefault();
                                                        joinFromPopup();
                                                    }}
                                                    style={{ display: 'grid', gap: '8px' }}
                                                >
                                                    <input
                                                        value={joinName}
                                                        onChange={(event) => setJoinName(event.target.value)}
                                                        placeholder="Auto-generated username"
                                                        style={{
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            border: `2px solid ${theme.secondary}`,
                                                            backgroundColor: theme.bgDark,
                                                            color: theme.textPrimary,
                                                            fontFamily: 'inherit'
                                                        }}
                                                    />
                                                    <input
                                                        value={joinCode}
                                                        onChange={(event) => setJoinCode(event.target.value)}
                                                        placeholder="Enter session code or link"
                                                        style={{
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            border: `2px solid ${theme.secondary}`,
                                                            backgroundColor: theme.bgDark,
                                                            color: theme.textPrimary,
                                                            fontFamily: 'inherit'
                                                        }}
                                                    />
                                                    <button
                                                        type="submit"
                                                        disabled={partyLoading}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px',
                                                            fontSize: '0.95rem',
                                                            backgroundColor: theme.secondary,
                                                            color: theme.textPrimary,
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: partyLoading ? 'not-allowed' : 'pointer',
                                                            fontFamily: 'inherit',
                                                            fontWeight: 'bold',
                                                            opacity: partyLoading ? 0.7 : 1
                                                        }}
                                                    >
                                                        {partyLoading ? 'Joining...' : 'Join Session'}
                                                    </button>
                                                </form>
                                            </>
                                        )}

                                        {(partyError || joinAlias) && (
                                            <p style={{ marginBottom: 0, marginTop: '10px', color: partyError ? theme.danger : theme.success, fontSize: '0.85rem' }}>
                                                {partyError || joinAlias}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'chat' && (
                                    <div>
                                        <h3 style={{ marginTop: 0, fontSize: '1.1rem', color: theme.textPrimary }}>Group Chat</h3>
                                        {hasActiveSessionContext ? (
                                            <div style={{
                                                backgroundColor: theme.bgDark,
                                                border: `1px solid ${theme.bgLight}`,
                                                borderRadius: '10px',
                                                padding: '12px',
                                                display: 'grid',
                                                gap: '10px'
                                            }}>
                                                <div style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>Active Session</div>
                                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{resolvedSessionContext.code}</div>
                                                <div style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>
                                                    {isHostContext
                                                        ? `Hosting as ${resolvedSessionContext.alias || 'Host'}`
                                                        : `Joined as ${resolvedSessionContext.alias || 'Guest'}`}
                                                </div>
                                                <div style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>
                                                    Participants online: {connectedParticipants.length}/{activeParticipants.length || 0} • Join {sessionJoinLocked ? 'Locked' : 'Open'}
                                                </div>
                                                <div style={{
                                                    maxHeight: '78px',
                                                    overflowY: 'auto',
                                                    border: `1px solid ${theme.bgLight}`,
                                                    borderRadius: '8px',
                                                    padding: '8px',
                                                    backgroundColor: theme.bgMedium,
                                                    display: 'grid',
                                                    gap: '4px'
                                                }}>
                                                    {activeParticipants.length === 0 ? (
                                                        <div style={{ color: theme.textSecondary, fontSize: '0.82rem' }}>No active participants.</div>
                                                    ) : (
                                                        activeParticipants.map((participant) => (
                                                            <div key={participant.id} style={{ fontSize: '0.82rem' }}>
                                                                <strong>{participant.alias}</strong>{' '}
                                                                <span style={{ color: participant.connected ? theme.success : theme.warning }}>
                                                                    {participant.connected ? 'Online' : 'Idle'}
                                                                </span>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                                <div style={{
                                                    maxHeight: '180px',
                                                    overflowY: 'auto',
                                                    border: `1px solid ${theme.bgLight}`,
                                                    borderRadius: '8px',
                                                    padding: '8px',
                                                    backgroundColor: theme.bgMedium,
                                                    display: 'grid',
                                                    gap: '6px'
                                                }}>
                                                    {messages.length === 0 ? (
                                                        <div style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>No messages yet.</div>
                                                    ) : (
                                                        messages.slice(-60).map((message) => (
                                                            <div key={message.id} style={{ fontSize: '0.85rem' }}>
                                                                <strong>{message.alias}: </strong>
                                                                <span>{message.body}</span>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <input
                                                        value={chatInput}
                                                        onChange={(event) => setChatInput(event.target.value)}
                                                        onKeyDown={(event) => event.key === 'Enter' && handleSendChat()}
                                                        placeholder="Type a message"
                                                        style={{
                                                            flex: 1,
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            border: `2px solid ${theme.secondary}`,
                                                            backgroundColor: theme.bgMedium,
                                                            color: theme.textPrimary,
                                                            fontFamily: 'inherit'
                                                        }}
                                                    />
                                                    <button
                                                        onClick={handleSendChat}
                                                        disabled={sendingChat}
                                                        style={{
                                                            padding: '10px 12px',
                                                            fontSize: '0.9rem',
                                                            backgroundColor: theme.accent,
                                                            color: theme.textPrimary,
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: sendingChat ? 'not-allowed' : 'pointer',
                                                            fontFamily: 'inherit',
                                                            fontWeight: 'bold',
                                                            opacity: sendingChat ? 0.7 : 1
                                                        }}
                                                    >
                                                        {sendingChat ? 'Sending...' : 'Send'}
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={copySessionLink}
                                                    style={{
                                                        width: '100%',
                                                        padding: '9px',
                                                        fontSize: '0.9rem',
                                                        backgroundColor: 'transparent',
                                                        color: theme.textPrimary,
                                                        border: `1px solid ${theme.secondary}`,
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'inherit',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Copy Join Link
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <p style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
                                                    No active session detected. Create or join a party session first.
                                                </p>
                                                <button
                                                    onClick={() => setActiveTab('party')}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        fontSize: '0.95rem',
                                                        backgroundColor: theme.accent,
                                                        color: theme.textPrimary,
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'inherit',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Go to Party Tab
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'voice' && (
                                    <div>
                                        <h3 style={{ marginTop: 0, fontSize: '1.1rem', color: theme.textPrimary }}>Voice Chat</h3>
                                        {hasActiveSessionContext ? (
                                            <div style={{
                                                backgroundColor: theme.bgDark,
                                                border: `1px solid ${theme.bgLight}`,
                                                borderRadius: '10px',
                                                padding: '12px',
                                                display: 'grid',
                                                gap: '9px'
                                            }}>
                                                <div style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>Active Session</div>
                                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{resolvedSessionContext.code}</div>
                                                <div style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>
                                                    {isHostContext
                                                        ? `Hosting as ${resolvedSessionContext.alias || 'Host'}`
                                                        : `Joined as ${resolvedSessionContext.alias || 'Guest'}`}
                                                </div>
                                                <div style={{ color: theme.textSecondary, fontSize: '0.82rem' }}>
                                                    Join: {sessionJoinLocked ? 'Locked' : 'Open'} • Online: {connectedParticipants.length}/{activeParticipants.length || 0}
                                                </div>
                                                <div style={{ color: theme.textSecondary, fontSize: '0.82rem' }}>
                                                    Transport: WebRTC VOIP {hasTurnRelay ? '(STUN + TURN relay)' : '(STUN only - add TURN env vars for full NAT coverage)'}
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    {!voiceEnabled ? (
                                                        <button
                                                            onClick={() => {
                                                                enableVoice().catch((error) => setPartyError(error.message || 'Could not enable voice'));
                                                            }}
                                                            style={{
                                                                padding: '10px 14px',
                                                                fontSize: '0.9rem',
                                                                backgroundColor: theme.accent,
                                                                color: theme.textPrimary,
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontFamily: 'inherit',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            Enable Voice
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    disableVoice().catch((error) => setPartyError(error.message || 'Could not disable voice'));
                                                                }}
                                                                style={{
                                                                    padding: '10px 14px',
                                                                    fontSize: '0.9rem',
                                                                    backgroundColor: theme.danger,
                                                                    color: theme.textPrimary,
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'inherit',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                Leave Voice
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    toggleMute().catch((error) => setPartyError(error.message || 'Failed to toggle mute'));
                                                                }}
                                                                style={{
                                                                    padding: '10px 14px',
                                                                    fontSize: '0.9rem',
                                                                    backgroundColor: localMuted ? theme.success : theme.warning,
                                                                    color: theme.textPrimary,
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'inherit',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {localMuted ? 'Unmute' : 'Mute'}
                                                            </button>
                                                            <button
                                                                onClick={toggleDeafen}
                                                                style={{
                                                                    padding: '10px 14px',
                                                                    fontSize: '0.9rem',
                                                                    backgroundColor: deafened ? theme.success : theme.secondary,
                                                                    color: theme.textPrimary,
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'inherit',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {deafened ? 'Undeafen' : 'Deafen'}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                <div style={{
                                                    color: localSpeaking && !localMuted ? theme.success : theme.textSecondary,
                                                    fontSize: '0.85rem',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {voiceEnabled
                                                        ? (localSpeaking && !localMuted ? 'You are speaking' : 'You are quiet')
                                                        : 'Voice disabled'}
                                                </div>
                                                <div style={{ display: 'grid', gap: '4px' }}>
                                                    {remoteVoices.length === 0 ? (
                                                        <div style={{ color: theme.textSecondary, fontSize: '0.82rem' }}>
                                                            No remote voices connected yet.
                                                        </div>
                                                    ) : (
                                                        remoteVoices.map((voice) => (
                                                            <div key={voice.alias} style={{ fontSize: '0.82rem' }}>
                                                                <strong>{voice.alias}</strong>{' '}
                                                                <span style={{ color: remoteMuteStatus[voice.alias] ? theme.warning : theme.textSecondary }}>
                                                                    {remoteMuteStatus[voice.alias] ? 'Muted' : 'Unmuted'}
                                                                </span>{' '}
                                                                <span style={{ color: activeSpeakers[voice.alias] ? theme.success : theme.textSecondary }}>
                                                                    {activeSpeakers[voice.alias] ? 'Speaking' : 'Quiet'}
                                                                </span>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <p style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
                                                    No active session detected. Create or join a party session first.
                                                </p>
                                                <button
                                                    onClick={() => setActiveTab('party')}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        fontSize: '0.95rem',
                                                        backgroundColor: theme.accent,
                                                        color: theme.textPrimary,
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'inherit',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Go to Party Tab
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}

export default SocialButton;
